(() => {
  function initUtilities(root) {
    if (root.dataset.pippaUtilitiesReady) return;
    root.dataset.pippaUtilitiesReady = "true";

    const overlay = root.querySelector("[data-pippa-utility-overlay]");
    const drawers = [...root.querySelectorAll("[data-pippa-utility]")];
    const locationSheet = root.querySelector("[data-pippa-locale-popover]");
    const locationTriggers = [...root.querySelectorAll("[data-pippa-locale]")];
    let activeDrawer = null;

    function closeLocation() {
      if (!locationSheet || locationSheet.hidden) return;
      locationSheet.hidden = true;
      document.body.classList.remove("pippa-location-open");
      locationTriggers.forEach((trigger) => trigger.setAttribute("aria-expanded", "false"));
    }

    function openLocation() {
      if (!locationSheet) return;
      root.querySelector("[data-pippa-drawer]")?.setAttribute("hidden", "");
      closeUtility();
      locationSheet.hidden = false;
      document.body.classList.add("pippa-location-open");
      locationTriggers.forEach((trigger) => trigger.setAttribute("aria-expanded", "true"));
      window.requestAnimationFrame(() => locationSheet.querySelector("[data-pippa-country-search]")?.focus({ preventScroll: true }));
    }

    function closeUtility() {
      if (!activeDrawer) return;
      const closing = activeDrawer;
      activeDrawer = null;
      closing.classList.remove("is-open");
      closing.style.transform = "translateX(100%)";
      closing.setAttribute("aria-hidden", "true");
      overlay?.classList.remove("is-open");
      if (overlay) overlay.style.opacity = "0";
      document.body.classList.remove("pippa-utility-open");
      window.setTimeout(() => {
        if (!closing.classList.contains("is-open")) {
          closing.hidden = true;
          closing.style.transform = "";
        }
        if (!activeDrawer && overlay) {
          overlay.hidden = true;
          overlay.style.opacity = "";
        }
      }, 260);
    }

    function openUtility(name) {
      const drawer = drawers.find((item) => item.dataset.pippaUtility === name);
      if (!drawer) return;
      closeLocation();
      if (activeDrawer && activeDrawer !== drawer) {
        activeDrawer.classList.remove("is-open");
        activeDrawer.hidden = true;
      }
      activeDrawer = drawer;
      drawer.hidden = false;
      overlay.hidden = false;
      drawer.setAttribute("aria-hidden", "false");
      document.body.classList.add("pippa-utility-open");
      drawer.getBoundingClientRect();
      drawer.classList.add("is-open");
      drawer.style.transform = "translateX(0)";
      overlay.classList.add("is-open");
      overlay.style.opacity = "1";
      drawer.querySelector("input, button, a")?.focus({ preventScroll: true });
    }

    root.querySelectorAll("[data-pippa-utility-open]").forEach((trigger) => {
      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        root.querySelector("[data-pippa-drawer]")?.setAttribute("hidden", "");
        openUtility(trigger.dataset.pippaUtilityOpen);
      });
    });
    root.querySelectorAll("[data-pippa-utility-close]").forEach((button) => button.addEventListener("click", closeUtility));
    overlay?.addEventListener("click", closeUtility);
    locationTriggers.forEach((trigger) => trigger.addEventListener("click", (event) => {
      event.preventDefault();
      locationSheet?.hidden ? openLocation() : closeLocation();
    }));
    root.querySelector("[data-pippa-locale-close]")?.addEventListener("click", closeLocation);
    root.querySelector("[data-pippa-country-search]")?.addEventListener("input", (event) => {
      const query = event.currentTarget.value.trim().toLowerCase();
      root.querySelectorAll("[data-pippa-country-option]").forEach((option) => {
        option.hidden = Boolean(query) && !option.dataset.pippaCountryQuery.toLowerCase().includes(query);
      });
    });
    root.querySelectorAll("[data-pippa-country-option]").forEach((option) => {
      option.addEventListener("click", () => {
        const form = option.closest("form");
        const input = form?.querySelector("[data-pippa-country-input]");
        if (!form || !input) return;
        const countryCode = option.dataset.pippaCountryOption;
        const languageSelect = form.querySelector('select[name="language_code"]');
        const languageByCountry = {
          ES: "es", MX: "es", AR: "es", CL: "es", CO: "es", PE: "es",
          FR: "fr", BE: "fr", CH: "de", DE: "de", AT: "de",
          IT: "it", PT: "pt-PT", BR: "pt-PT", CN: "zh-CN",
          US: "en", GB: "en", CA: "en", AU: "en", NZ: "en", IE: "en"
        };
        const preferredLanguage = languageByCountry[countryCode] || "en";
        input.value = countryCode;
        if (languageSelect?.querySelector(`option[value="${preferredLanguage}"]`)) languageSelect.value = preferredLanguage;
        localStorage.setItem("pippa-localization-manual", "true");
        form.submit();
      });
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        closeUtility();
        closeLocation();
      }
    });
    document.addEventListener("pointerdown", (event) => {
      if (!locationSheet || locationSheet.hidden || locationSheet.contains(event.target) || locationTriggers.some((trigger) => trigger.contains(event.target))) return;
      closeLocation();
    });

    root.querySelectorAll("[data-pippa-localization]").forEach((select) => {
      select.addEventListener("change", () => {
        localStorage.setItem("pippa-localization-manual", "true");
        select.form?.submit();
      });
    });
  }

  async function resolveLocalization() {
    if (!window.Shopify || window.Shopify.designMode) return;
    if (localStorage.getItem("pippa-localization-manual") || sessionStorage.getItem("pippa-geo-resolved-v5")) return;
    sessionStorage.setItem("pippa-geo-resolved-v5", "true");
    try {
      const base = window.Shopify.routes?.root || "/";
      const currentCountry = window.Shopify.country || "";
      const currentLanguage = window.Shopify.language || "";
      const url = `${base}browsing_context_suggestions.json?country[enabled]=true&country[exclude]=${encodeURIComponent(currentCountry)}&language[enabled]=true&language[exclude]=${encodeURIComponent(currentLanguage)}`;
      const response = await fetch(url, { headers: { Accept: "application/json" } });
      if (!response.ok) return;
      const data = await response.json();
      const suggestion = data.suggestions?.find((item) => Number(item.confidence || 0) >= 1);
      if (!suggestion) return;
      const country = suggestion.parts?.country?.handle || currentCountry;
      const language = suggestion.parts?.language?.handle || currentLanguage;
      if ((!country || country === currentCountry) && (!language || language === currentLanguage)) return;
      const form = document.createElement("form");
      form.action = `${base}localization`;
      form.method = "post";
      form.hidden = true;
      [["_method", "PUT"], ["country_code", country], ["language_code", language], ["return_to", `${location.pathname}${location.search}`]].forEach(([name, value]) => {
        const input = document.createElement("input");
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (_) {
      /* Native Shopify Markets context remains the fallback. */
    }
  }

  function initAllUtilities() {
    document.querySelectorAll(".pippaexact-header-section .pippa-exact-site").forEach(initUtilities);
    resolveLocalization();
  }

  document.readyState === "loading" ? document.addEventListener("DOMContentLoaded", initAllUtilities) : initAllUtilities();
  document.addEventListener("shopify:section:load", initAllUtilities);
})();
